import { useState } from "react";
import "@/App.css";
import { Mail, Download, ChevronDown, Briefcase, Users, TrendingUp, Target, Award, MapPin, Phone, Calendar, Linkedin, ArrowRight, ChevronUp, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

// Resume download URL - Updated PDF version
const RESUME_URL = "https://customer-assets.emergentagent.com/job_kevin-cowen-exec/artifacts/kiiuyaui_KEVIN%20B%20COWEN%20Resume_2025.pdf";

// Kevin's photo URL
const PHOTO_URL = "https://customer-assets.emergentagent.com/job_351dd523-2f70-40b5-8070-410e2cf77980/artifacts/x325vn3f_generated-image-2.png";

// LinkedIn URL with messaging
const LINKEDIN_URL = "https://www.linkedin.com/in/kevin-cowen-5192361b0/";
const LINKEDIN_MESSAGE_URL = "https://www.linkedin.com/messaging/compose/?recipient=kevin-cowen-5192361b0";

// Light gray background color
const LIGHT_GRAY_BG = "#f7f7f8";

// Smooth scroll to section
const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

// Section Divider Component with breadcrumb label
const SectionDivider = ({ nextSection, label = "Impact" }) => {
  return (
    <div className="flex justify-center py-8 relative z-20">
      <button 
        onClick={() => scrollToSection(nextSection)}
        className="flex flex-col items-center gap-2 text-white/40 hover:text-white/60 transition-colors group bg-[#0a0c10]/80 px-4 py-2 rounded-full backdrop-blur-sm"
        aria-label={`Scroll to ${nextSection} section`}
      >
        <span className="text-xs uppercase tracking-widest">{label}</span>
        <ChevronDown className="w-5 h-5 animate-bounce" aria-hidden="true" />
      </button>
    </div>
  );
};

// Header Component with LinkedIn icon and mobile menu
const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleNavClick = (sectionId) => {
    scrollToSection(sectionId);
    setMobileMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0c10]/90 backdrop-blur-md border-b border-white/10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <button 
            onClick={() => handleNavClick('hero')}
            className="font-bold text-xl hover:opacity-80 transition-opacity duration-200"
            data-testid="header-name"
            aria-label="Go to top of page"
          >
            <span className="text-white">K</span>
            <span className="text-[#60a5fa]">C</span>
          </button>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
            <button 
              onClick={() => handleNavClick('about')}
              className="text-sm font-medium text-white/70 hover:text-white transition-colors duration-200"
              data-testid="nav-about"
            >
              About
            </button>
            <button 
              onClick={() => handleNavClick('experience')}
              className="text-sm font-medium text-white/70 hover:text-white transition-colors duration-200"
              data-testid="nav-experience"
            >
              Experience
            </button>
            <button 
              onClick={() => handleNavClick('contact')}
              className="text-sm font-medium text-white/70 hover:text-white transition-colors duration-200"
              data-testid="nav-contact"
            >
              Contact
            </button>
          </nav>
          
          <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3">
            <a
              href={LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 sm:p-2 text-[#60a5fa] hover:text-[#93c5fd] transition-colors duration-200"
              data-testid="header-linkedin"
              aria-label="Connect with Kevin Cowen on LinkedIn (opens in new tab)"
            >
              <Linkedin className="w-4 h-4 sm:w-5 sm:h-5" />
            </a>
            <Button 
              size="sm"
              onClick={() => window.open(RESUME_URL, '_blank')}
              className="bg-transparent border border-white/20 text-white hover:bg-white/10 hover:border-white/40 px-2 sm:px-3 py-1.5 text-xs sm:text-sm"
              data-testid="header-download-resume"
            >
              <Download className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Resume</span>
            </Button>
            <Button 
              size="sm"
              onClick={() => handleNavClick('contact')}
              className="bg-gradient-to-r from-[#3b82f6] to-[#60a5fa] hover:from-[#2563eb] hover:to-[#3b82f6] text-white border-0 shadow-lg shadow-blue-500/25 px-2 sm:px-3 py-1.5 text-xs sm:text-sm"
              data-testid="header-contact-button"
            >
              <Mail className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Let's Talk</span>
            </Button>
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-white/70 hover:text-white transition-colors"
              data-testid="mobile-menu-toggle"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-16 left-0 right-0 bg-[#0a0c10]/98 backdrop-blur-lg border-b border-white/10">
          <nav className="flex flex-col py-4 px-4" aria-label="Mobile navigation">
            <button 
              onClick={() => handleNavClick('about')}
              className="py-3 text-left text-base font-medium text-white/80 hover:text-white transition-colors border-b border-white/5"
              data-testid="mobile-nav-about"
            >
              About
            </button>
            <button 
              onClick={() => handleNavClick('experience')}
              className="py-3 text-left text-base font-medium text-white/80 hover:text-white transition-colors border-b border-white/5"
              data-testid="mobile-nav-experience"
            >
              Experience
            </button>
            <button 
              onClick={() => handleNavClick('education')}
              className="py-3 text-left text-base font-medium text-white/80 hover:text-white transition-colors border-b border-white/5"
              data-testid="mobile-nav-education"
            >
              Education
            </button>
            <button 
              onClick={() => handleNavClick('contact')}
              className="py-3 text-left text-base font-medium text-white/80 hover:text-white transition-colors"
              data-testid="mobile-nav-contact"
            >
              Contact
            </button>
            <div className="pt-4">
              <Button 
                size="sm"
                onClick={() => {
                  window.open(RESUME_URL, '_blank');
                  setMobileMenuOpen(false);
                }}
                className="w-full bg-gradient-to-r from-[#3b82f6] to-[#60a5fa] hover:from-[#2563eb] hover:to-[#3b82f6] text-white border-0"
                data-testid="mobile-download-resume"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Resume
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

// Hero Section - Desktop: background photo, Mobile: profile photo above text
const Hero = () => {
  return (
    <section 
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
      aria-label="Hero section introducing Kevin Cowen"
    >
      {/* Base Background */}
      <div className="absolute inset-0 bg-[#0a0c10]" />
      
      {/* Blueprint Grid */}
      <div className="absolute inset-0 blueprint-grid" />
      
      {/* Photo Container - Only visible on large screens */}
      <div className="hidden lg:block absolute left-0 top-0 bottom-0 w-[50%]">
        <img 
          src={PHOTO_URL}
          alt="Professional headshot of Kevin Cowen, Sales & Operations Executive"
          className="absolute inset-0 w-full h-full object-cover object-[center_20%]"
        />
        {/* Horizontal fade to right */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent via-60% to-[#0a0c10]" />
        {/* Top/bottom vignette */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0c10] via-transparent via-30% to-[#0a0c10]/60" />
      </div>
      
      {/* Content */}
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-24 pb-24 lg:pt-28 lg:pb-0 w-full">
        <div className="flex flex-col lg:flex-row items-center lg:items-center">
          {/* Spacer for photo area - Desktop only */}
          <div className="hidden lg:block lg:w-[50%]" />
          
          {/* Mobile Profile Photo - Circular */}
          <div className="lg:hidden mb-8 relative">
            <div className="w-40 h-40 sm:w-48 sm:h-48 rounded-full overflow-hidden border-4 border-white/10 shadow-2xl shadow-blue-500/20">
              <img 
                src={PHOTO_URL}
                alt="Professional headshot of Kevin Cowen"
                className="w-full h-full object-cover object-[center_20%]"
              />
            </div>
            {/* Decorative ring */}
            <div className="absolute -inset-2 rounded-full border border-[#3b82f6]/30 animate-pulse" aria-hidden="true" />
          </div>
          
          {/* Right Content */}
          <div className="lg:w-[50%] space-y-6 lg:space-y-8 text-center lg:text-left lg:pl-12">
            {/* Eyebrow */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-[#0a0c10]/80 border border-white/10 rounded-full backdrop-blur-sm">
              <span className="w-2 h-2 bg-[#60a5fa] rounded-full animate-pulse" aria-hidden="true" />
              <span className="text-xs sm:text-sm text-white/70">Open to New Opportunities</span>
            </div>
            
            {/* Main Headline */}
            <div className="space-y-2 sm:space-y-4">
              <h1 
                className="text-4xl sm:text-5xl lg:text-7xl xl:text-8xl font-bold tracking-tight"
                data-testid="hero-name"
              >
                <span className="text-white">Kevin</span>
                <br />
                <span className="bg-gradient-to-r from-[#3b82f6] via-[#60a5fa] to-[#93c5fd] bg-clip-text text-transparent">Cowen</span>
              </h1>
              <p className="text-lg sm:text-xl lg:text-3xl text-white/60 font-light">
                Sales & Operations Executive
              </p>
            </div>
            
            {/* Value Proposition */}
            <p className="text-base sm:text-lg lg:text-xl text-white/70 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              15+ years transforming sales organizations. Building high-performing teams. 
              <span className="text-white font-medium"> Delivering $10M+ in sustainable revenue growth.</span>
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-3 sm:gap-4 pt-2 sm:pt-4">
              <Button 
                size="lg"
                onClick={() => scrollToSection('contact')}
                className="group bg-gradient-to-r from-[#3b82f6] to-[#60a5fa] hover:from-[#2563eb] hover:to-[#3b82f6] text-white px-6 py-5 sm:px-8 sm:py-6 text-base sm:text-lg shadow-xl shadow-blue-500/25 border-0"
                data-testid="hero-start-conversation-button"
              >
                Start a Conversation
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => window.open(RESUME_URL, '_blank')}
                className="border-white/20 text-white hover:bg-white/10 hover:border-white/40 px-6 py-5 sm:px-8 sm:py-6 text-base sm:text-lg backdrop-blur-sm"
                data-testid="hero-download-resume-button"
              >
                <Download className="w-4 h-4 sm:w-5 sm:h-5 mr-2" aria-hidden="true" />
                Download Resume
              </Button>
            </div>
            
            {/* Quick Stats */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-6 sm:gap-8 pt-6 sm:pt-8 border-t border-white/10">
              <div className="text-center lg:text-left">
                <div className="text-2xl sm:text-3xl font-bold text-white">15+</div>
                <div className="text-xs sm:text-sm text-white/50">Years Experience</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl sm:text-3xl font-bold text-white">$10M+</div>
                <div className="text-xs sm:text-sm text-white/50">Revenue Growth</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl sm:text-3xl font-bold text-white">80+</div>
                <div className="text-xs sm:text-sm text-white/50">Team Members Led</div>
              </div>
            </div>
            
            {/* Scroll Indicator - Inline, not absolute positioned */}
            <div className="pt-8 lg:pt-12">
              <button 
                onClick={() => scrollToSection('highlights')}
                className="inline-flex flex-col items-center gap-2 text-white/40 hover:text-white/60 transition-colors mx-auto lg:mx-0"
                data-testid="hero-scroll-down"
                aria-label="Scroll to career impact section"
              >
                <span className="text-xs uppercase tracking-widest">Impact</span>
                <ChevronDown className="w-5 h-5 animate-bounce" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Flip Card Component - Touch-friendly for mobile
const FlipCard = ({ metric, icon: Icon, description }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  
  return (
    <div 
      className="flip-card-container h-44 sm:h-48 cursor-pointer"
      onMouseEnter={() => setIsFlipped(true)}
      onMouseLeave={() => setIsFlipped(false)}
      onClick={() => setIsFlipped(!isFlipped)}
      data-testid="metric-card"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && setIsFlipped(!isFlipped)}
      aria-label={`${metric.value} ${metric.label}. Tap for more details.`}
    >
      <div className={`flip-card-inner ${isFlipped ? 'flipped' : ''}`}>
        {/* Front */}
        <div 
          className="flip-card-front rounded-xl sm:rounded-2xl p-4 sm:p-6 flex flex-col items-center justify-center border border-white/10"
          style={{ backgroundColor: '#1a2332' }}
        >
          <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-[#60a5fa] mb-2 sm:mb-3" aria-hidden="true" />
          <div className="text-3xl sm:text-4xl font-bold text-white">{metric.value}</div>
          <div className="text-xs sm:text-sm text-white/60 mt-1 sm:mt-2 text-center">{metric.label}</div>
          <div className="text-[10px] sm:hidden text-white/40 mt-2">Tap for details</div>
        </div>
        
        {/* Back */}
        <div 
          className="flip-card-back rounded-xl sm:rounded-2xl p-4 sm:p-6 flex flex-col items-center justify-center border border-[#3b82f6]/30"
          style={{ backgroundColor: '#1e3a5f' }}
        >
          <p className="text-white/90 text-center text-xs sm:text-sm leading-relaxed">{description}</p>
        </div>
      </div>
    </div>
  );
};

// Highlights Section
const Highlights = () => {
  const metrics = [
    { 
      value: "$10M+", 
      label: "Annual Revenue Growth", 
      icon: TrendingUp,
      description: "Consistently exceeded revenue targets through strategic account management and consultative selling approaches."
    },
    { 
      value: "15+", 
      label: "Years of Experience", 
      icon: Calendar,
      description: "Proven track record across regulated industries, complex markets, and high-growth environments."
    },
    { 
      value: "80+", 
      label: "Team Members Led", 
      icon: Users,
      description: "Directed the largest Northeast operation, building customer-first cultures and high-performing teams."
    },
    { 
      value: "250+", 
      label: "Accounts Managed", 
      icon: Target,
      description: "Consulted with enterprise accounts to introduce cutting-edge solutions, converting prospects into long-term partners."
    },
    { 
      value: "17%", 
      label: "Year-over-Year Growth", 
      icon: Award,
      description: "Accelerated fiscal growth through data-driven decision-making and personalized sales coaching."
    },
    { 
      value: "20+", 
      label: "Promotions Mentored", 
      icon: Briefcase,
      description: "Developed emerging leaders through mentorship programs, professional development, and succession planning."
    },
  ];

  return (
    <section id="highlights" className="py-16 sm:py-24 relative" aria-labelledby="highlights-title">
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-16">
          <span className="text-[#60a5fa] text-xs sm:text-sm font-semibold uppercase tracking-widest">Impact</span>
          <h2 id="highlights-title" className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white mt-3 sm:mt-4 mb-4 sm:mb-6" data-testid="highlights-title">
            Career Impact at a Glance
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto text-sm sm:text-lg px-4">
            Quantified results from 15+ years of sales leadership. 
            <span className="hidden sm:inline text-white/80"> Hover over the cards to learn more.</span>
            <span className="sm:hidden text-white/80"> Tap the cards to learn more.</span>
          </p>
        </div>
        
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {metrics.map((metric, index) => (
            <FlipCard 
              key={index}
              metric={metric}
              icon={metric.icon}
              description={metric.description}
            />
          ))}
        </div>
        
        {/* Section Divider */}
        <SectionDivider nextSection="about" label="About" />
      </div>
    </section>
  );
};

// About Section with updated narrative
const About = () => {
  return (
    <section id="about" className="py-12 sm:py-16 relative scroll-mt-16" aria-labelledby="about-title">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Floating Card Container - Light Gray Background */}
        <div className="rounded-2xl sm:rounded-3xl shadow-2xl shadow-black/20 p-5 sm:p-8 lg:p-16" style={{ backgroundColor: LIGHT_GRAY_BG }}>
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-16">
            {/* Left - Visual (Sticky on desktop) */}
            <div className="lg:w-[45%] order-2 lg:order-1">
              <div className="lg:sticky lg:top-24 bg-[#0f1218] rounded-xl sm:rounded-2xl p-5 sm:p-8 lg:p-10">
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#3b82f6] to-[#60a5fa] rounded-lg sm:rounded-xl flex items-center justify-center" aria-hidden="true">
                      <span className="text-base sm:text-xl font-bold text-white">K</span>
                      <span className="text-base sm:text-xl font-bold text-[#93c5fd]">C</span>
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold text-white">Kevin Cowen</h3>
                      <p className="text-sm sm:text-base text-white/60">Sales & Operations Executive</p>
                    </div>
                  </div>
                  
                  <Separator className="bg-white/10" />
                  
                  <div className="grid grid-cols-2 gap-2 sm:gap-4">
                    <div className="bg-white/5 rounded-lg sm:rounded-xl p-3 sm:p-4">
                      <div className="text-xl sm:text-2xl font-bold text-[#60a5fa]">15+</div>
                      <div className="text-xs sm:text-sm text-white/60">Years Leading</div>
                    </div>
                    <div className="bg-white/5 rounded-lg sm:rounded-xl p-3 sm:p-4">
                      <div className="text-xl sm:text-2xl font-bold text-[#60a5fa]">$10M+</div>
                      <div className="text-xs sm:text-sm text-white/60">Revenue Driven</div>
                    </div>
                    <div className="bg-white/5 rounded-lg sm:rounded-xl p-3 sm:p-4">
                      <div className="text-xl sm:text-2xl font-bold text-[#60a5fa]">80+</div>
                      <div className="text-xs sm:text-sm text-white/60">Team Size</div>
                    </div>
                    <div className="bg-white/5 rounded-lg sm:rounded-xl p-3 sm:p-4">
                      <div className="text-xl sm:text-2xl font-bold text-[#60a5fa]">20+</div>
                      <div className="text-xs sm:text-sm text-white/60">Promotions</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right - Content */}
            <div className="lg:w-[55%] space-y-6 sm:space-y-8 order-1 lg:order-2">
              <div>
                <span className="text-[#3b82f6] text-xs sm:text-sm font-semibold uppercase tracking-widest">About</span>
                <h2 id="about-title" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0f1218] mt-3 sm:mt-4" data-testid="about-title">
                  Building Teams That Deliver Results
                </h2>
              </div>
              
              <div className="space-y-4 sm:space-y-6 text-[#0f1218]/70 text-sm sm:text-base lg:text-lg leading-relaxed">
                <p>
                  I'm a sales and operations leader with more than <strong className="text-[#0f1218]">15 years of experience</strong> building teams, growing revenue, and establishing the kind of operational discipline that produces consistent, sustainable results.
                </p>
                <p>
                  My career has spanned <strong className="text-[#0f1218]">regulated industries, complex product environments, and high-growth markets</strong>. I've managed teams of 80+ employees, led territory expansion that generated over $10 million in annual revenue, and developed leaders who have gone on to advance across their organizations.
                </p>
                <p>
                  I believe in bringing <strong className="text-[#0f1218]">structure, accountability, and trust</strong> to every team I lead. I focus on outcomes—not activities—and I measure success by the long-term value created for customers, employees, and the business.
                </p>
              </div>
              
              <div>
                <h3 className="font-bold text-[#0f1218] mb-3 sm:mb-4 text-base sm:text-lg">Core Competencies</h3>
                <div className="flex flex-wrap gap-1.5 sm:gap-2" data-testid="skills-chip-group" role="list" aria-label="Core competencies">
                  {[
                    "Strategic Sales Planning",
                    "Revenue Operations",
                    "Territory Design",
                    "Team Development",
                    "CRM & Forecasting",
                    "Customer Success",
                    "Process Optimization",
                    "Market Expansion"
                  ].map((skill, index) => (
                    <span 
                      key={index} 
                      className="bg-[#e5e7eb] text-[#0f1218] px-2.5 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm rounded-full font-medium"
                      role="listitem"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Section Divider */}
        <SectionDivider nextSection="experience" label="Experience" />
      </div>
    </section>
  );
};

// Experience Card Component with Learn More expansion
const ExperienceCard = ({ exp }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <article 
      className="bg-white rounded-xl sm:rounded-2xl overflow-hidden border border-[#e2e8f0]"
      data-testid="experience-item-card"
    >
      <div className="h-1 bg-gradient-to-r from-[#3b82f6] to-[#60a5fa]" aria-hidden="true" />
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-2 sm:gap-4 mb-4 sm:mb-6">
          <div>
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#0f1218]">{exp.title}</h3>
            <p className="text-base sm:text-lg font-semibold text-[#3b82f6]">
              {exp.company}
            </p>
          </div>
          <div className="flex flex-row lg:flex-col gap-2 lg:gap-1 lg:items-end mt-1 lg:mt-0">
            <span className="text-sm sm:text-base text-[#0f1218]/60 font-medium">{exp.period}</span>
            {exp.location && (
              <span className="flex items-center gap-1 text-[#0f1218]/50 text-xs sm:text-sm">
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4" aria-hidden="true" />
                {exp.location}
              </span>
            )}
          </div>
        </div>
        
        {/* Primary highlights - always visible */}
        <ul className="space-y-2 sm:space-y-3" role="list">
          {exp.highlights.map((highlight, hIndex) => (
            <li key={hIndex} className="flex gap-3 sm:gap-4 text-[#0f1218]/70 text-sm sm:text-base">
              <span className="mt-1.5 sm:mt-2 w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-[#3b82f6] flex-shrink-0" aria-hidden="true" />
              <span className="leading-relaxed">{highlight}</span>
            </li>
          ))}
        </ul>
        
        {/* Expanded details - shown when Learn More is clicked */}
        {exp.expandedHighlights && exp.expandedHighlights.length > 0 && (
          <>
            {isExpanded && (
              <ul className="space-y-2 sm:space-y-3 mt-2 sm:mt-3" role="list">
                {exp.expandedHighlights.map((highlight, hIndex) => (
                  <li key={hIndex} className="flex gap-3 sm:gap-4 text-[#0f1218]/70 text-sm sm:text-base">
                    <span className="mt-1.5 sm:mt-2 w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-[#60a5fa] flex-shrink-0" aria-hidden="true" />
                    <span className="leading-relaxed">{highlight}</span>
                  </li>
                ))}
              </ul>
            )}
            
            {/* Learn More / Show Less button */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-4 sm:mt-6 flex items-center gap-2 text-[#3b82f6] hover:text-[#2563eb] font-medium text-sm sm:text-base transition-colors"
              data-testid="learn-more-button"
              aria-expanded={isExpanded}
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Learn More
                </>
              )}
            </button>
          </>
        )}
      </div>
    </article>
  );
};

// Experience Section
const Experience = () => {
  const experiences = [
    {
      company: "Future Energy",
      title: "Outside Sales Account Executive",
      period: "Oct 2021 – June 2024",
      location: null,
      highlights: [
        "Drove revenue beyond $10 million annually by identifying growth opportunities and tailoring solutions to client needs across 250+ targeted accounts",
        "Developed data-driven account intelligence strategies that deepened client relationships and sustained high satisfaction scores"
      ],
      expandedHighlights: [
        "Consulted with over 250 targeted accounts to introduce a portfolio of cutting-edge energy and EV technology solutions, consistently converting prospects into long-term partners",
        "Collaborated with senior leadership to refine go-to-market strategies, pursue strategic vertical markets, and exceed monthly and quarterly sales quotas",
        "Improved CRM utilization, ensuring comprehensive account profiles and accurate sales forecasts that supported efficient resource allocation and decision-making"
      ]
    },
    {
      company: "Enterprise Rent-A-Car",
      title: "Area Manager",
      period: "May 2017 – April 2020",
      location: "Central & West, NJ",
      highlights: [
        "Led a diverse team of 40+ employees, implementing performance frameworks and mentorship programs that produced 20+ promotions",
        "Strengthened key stakeholder relationships, driving increased business volume and repeat referrals"
      ],
      expandedHighlights: [
        "Oversaw regional training sessions and prepared analytical performance reports that informed executive-level decision-making and strategic planning",
        "Identified operational inefficiencies and introduced process enhancements, improving profitability and overall customer satisfaction ratings",
        "Managed P&L responsibility across multiple locations while maintaining service excellence standards"
      ]
    },
    {
      company: "Enterprise Rent-A-Car",
      title: "Branch Rental Manager",
      period: "Oct 2015 – May 2017",
      location: "Newark Airport",
      highlights: [
        "Directed the largest Northeast operation at Newark Airport, managing a multi-brand fleet (National, Alamo, Enterprise) and supervising 80+ employees",
        "Achieved top-tier customer service metrics by streamlining workflows and embedding a customer-first culture"
      ],
      expandedHighlights: [
        "Executed staff training and leadership development initiatives that strengthened team competency, resilience, and adaptability in a high-traffic environment",
        "Optimized staffing models and scheduling to maximize efficiency during peak travel periods",
        "Coordinated cross-functional teams to ensure seamless operations across all three rental brands"
      ]
    },
    {
      company: "Enterprise Rent-A-Car",
      title: "Flagship Branch Manager",
      period: "Nov 2014 – Oct 2015",
      location: "Hackensack, NJ",
      highlights: [
        "Steered the Northeast's flagship operation to top rankings in service, growth, and operating profit",
        "Accelerated year-over-year revenue growth by 17% through data-driven decision-making and personalized sales coaching"
      ],
      expandedHighlights: [
        "Deployed targeted sales, marketing, and training strategies that consistently outperformed regional benchmarks",
        "Built a high-performance culture focused on accountability, continuous improvement, and customer satisfaction",
        "Mentored and developed team members, resulting in multiple internal promotions to management roles"
      ]
    },
    {
      company: "Enterprise Rent-A-Car",
      title: "Branch Manager & Assistant Manager",
      period: "Sept 2009 – Nov 2014",
      location: null,
      highlights: [
        "Managed multiple branches, overseeing daily operations, sales pipelines, and cost controls to consistently surpass targets",
        "Developed sales training programs and performance dashboards that measurably improved customer loyalty and efficiency"
      ],
      expandedHighlights: [
        "Implemented customer feedback loops that drove continuous service improvements and increased repeat business",
        "Managed fleet utilization and inventory to optimize profitability while meeting customer demand",
        "Progressive advancement from Management Trainee through Assistant Manager to Branch Manager roles"
      ]
    },
    {
      company: "CMT | Anheuser Busch / Farrell Distributing",
      title: "Promotions Manager",
      period: "2006 – 2007",
      location: "Killington, VT",
      highlights: [
        "Coordinated on-premise brand building events (bar takeovers, resort festivals), lifting weekly sales by up to 15% during peak winter season",
        "Negotiated prime display, cooler, and tap handle placements with 25+ bars and restaurants"
      ],
      expandedHighlights: [
        "Scheduled, trained, and supervised promotional staff to ensure flawless event activation and adherence to Vermont DLC regulations",
        "Partnered with Farrell Distributing sales reps to forecast inventory, align point of sale materials, and achieve monthly depletion targets",
        "Gathered consumer feedback and sell-through data, compiling actionable insights for Anheuser Busch regional marketing leadership"
      ]
    }
  ];

  return (
    <section id="experience" className="py-12 sm:py-16 relative scroll-mt-16" aria-labelledby="experience-title">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Floating Card Container - Light Gray Background */}
        <div className="rounded-2xl sm:rounded-3xl shadow-2xl shadow-black/20 p-5 sm:p-8 lg:p-16" style={{ backgroundColor: LIGHT_GRAY_BG }}>
          <div className="text-center mb-8 sm:mb-12">
            <span className="text-[#3b82f6] text-xs sm:text-sm font-semibold uppercase tracking-widest">Career</span>
            <h2 id="experience-title" className="text-2xl sm:text-3xl lg:text-5xl font-bold text-[#0f1218] mt-3 sm:mt-4 mb-4 sm:mb-6" data-testid="experience-title">
              Professional Experience
            </h2>
            <p className="text-[#0f1218]/60 max-w-2xl mx-auto text-sm sm:text-base lg:text-lg px-2">
              A progressive career in sales leadership, marked by consistent revenue growth, team development, and operational excellence.
            </p>
          </div>
          
          <div className="space-y-4 sm:space-y-6">
            {experiences.map((exp, index) => (
              <ExperienceCard key={index} exp={exp} />
            ))}
          </div>
        </div>
        
        {/* Section Divider */}
        <SectionDivider nextSection="education" label="Education" />
      </div>
    </section>
  );
};

// Education Section
const Education = () => {
  return (
    <section id="education" className="py-12 sm:py-16 relative scroll-mt-16" aria-labelledby="education-title">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Floating Card Container - Light Gray Background */}
        <div className="rounded-2xl sm:rounded-3xl shadow-2xl shadow-black/20 p-5 sm:p-8 lg:p-16" style={{ backgroundColor: LIGHT_GRAY_BG }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
            {/* Education */}
            <div>
              <span className="text-[#3b82f6] text-xs sm:text-sm font-semibold uppercase tracking-widest">Education</span>
              <h2 id="education-title" className="text-2xl sm:text-3xl font-bold text-[#0f1218] mt-3 sm:mt-4 mb-6 sm:mb-8" data-testid="education-title">
                Academic Background
              </h2>
              
              <article className="bg-white rounded-xl sm:rounded-2xl overflow-hidden border border-[#e2e8f0]" data-testid="education-item">
                <div className="h-1 bg-gradient-to-r from-[#3b82f6] to-[#60a5fa]" aria-hidden="true" />
                <div className="p-4 sm:p-6 lg:p-8">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold text-[#0f1218]">Bachelor of Arts</h3>
                      <p className="text-[#3b82f6] font-semibold text-sm sm:text-base">Professional Communications</p>
                      <p className="text-[#0f1218]/60 text-sm">Minor: Business Administration</p>
                    </div>
                    <div className="sm:text-right">
                      <p className="font-bold text-[#0f1218] text-sm sm:text-base">Plymouth State University</p>
                      <p className="text-[#0f1218]/60 flex items-center sm:justify-end gap-1 text-xs sm:text-sm">
                        <MapPin className="w-3 h-3 sm:w-4 sm:h-4" aria-hidden="true" />
                        Plymouth, NH
                      </p>
                    </div>
                  </div>
                </div>
              </article>
            </div>
            
            {/* Leadership & Community */}
            <div>
              <span className="text-[#3b82f6] text-xs sm:text-sm font-semibold uppercase tracking-widest">Community</span>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0f1218] mt-3 sm:mt-4 mb-6 sm:mb-8">
                Leadership & Giving Back
              </h2>
              
              <ul className="space-y-3 sm:space-y-4" role="list">
                {[
                  "Coordinated charitable events for United Way and March of Dimes, driving community engagement and brand goodwill",
                  "Organized breast cancer awareness campaigns, contributing to nonprofit initiatives and team morale",
                  "Served as a regional mentor to emerging leaders, guiding career development and succession planning"
                ].map((item, index) => (
                  <li key={index} className="flex gap-3 sm:gap-4 items-start">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 mt-2 rounded-full bg-[#3b82f6] flex-shrink-0" aria-hidden="true" />
                    <p className="text-[#0f1218]/70 leading-relaxed text-sm sm:text-base">{item}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        
        {/* Section Divider */}
        <SectionDivider nextSection="contact" label="Contact" />
      </div>
    </section>
  );
};

// Contact Section
const Contact = () => {
  return (
    <section id="contact" className="py-16 sm:py-24 relative scroll-mt-16" aria-labelledby="contact-title">
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <span className="text-[#60a5fa] text-xs sm:text-sm font-semibold uppercase tracking-widest">Contact</span>
          <h2 
            id="contact-title"
            className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white mt-3 sm:mt-4 mb-4 sm:mb-6"
            data-testid="contact-title"
          >
            Let's Start a Conversation
          </h2>
          <p className="text-white/60 mb-8 sm:mb-12 text-sm sm:text-base lg:text-lg leading-relaxed px-2">
            Whether you're looking for a sales leader to drive growth, an operations executive to bring structure, or simply want to connect—I'd be glad to hear from you.
          </p>
          
          {/* Contact Cards - Glass/Frosted Treatment */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12">
            <a 
              href="mailto:Kbcowen21@yahoo.com?subject=Let's Connect&body=Hi Kevin,%0D%0A%0D%0AI came across your profile and would like to discuss..."
              className="group rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:shadow-lg transition-all duration-300 glass-card"
              data-testid="contact-email"
              aria-label="Send email to Kevin Cowen"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#3b82f6] to-[#60a5fa] rounded-lg sm:rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform">
                <Mail className="w-6 h-6 sm:w-7 sm:h-7 text-white" aria-hidden="true" />
              </div>
              <div className="text-white font-semibold text-base sm:text-lg">Email</div>
              <div className="text-white/60 text-sm sm:text-base truncate">Kbcowen21@yahoo.com</div>
            </a>
            
            <a 
              href="tel:+12012476055"
              className="group rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:shadow-lg transition-all duration-300 glass-card"
              data-testid="contact-phone"
              aria-label="Call Kevin Cowen"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#3b82f6] to-[#60a5fa] rounded-lg sm:rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform">
                <Phone className="w-6 h-6 sm:w-7 sm:h-7 text-white" aria-hidden="true" />
              </div>
              <div className="text-white font-semibold text-base sm:text-lg">Phone</div>
              <div className="text-white/60 text-sm sm:text-base">(201) 247-6055</div>
            </a>
            
            <a 
              href={LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:shadow-lg transition-all duration-300 glass-card"
              data-testid="contact-linkedin"
              aria-label="Connect with Kevin Cowen on LinkedIn (opens in new tab)"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#3b82f6] to-[#60a5fa] rounded-lg sm:rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform">
                <Linkedin className="w-6 h-6 sm:w-7 sm:h-7 text-white" aria-hidden="true" />
              </div>
              <div className="text-white font-semibold text-base sm:text-lg">LinkedIn</div>
              <div className="text-white/60 text-sm sm:text-base">Connect with me</div>
            </a>
          </div>
          
          <div className="flex items-center gap-2 justify-center text-white/40 text-xs sm:text-sm mb-8 sm:mb-12">
            <MapPin className="w-3 h-3 sm:w-4 sm:h-4" aria-hidden="true" />
            <span>Clinton, NJ</span>
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Button 
              size="lg"
              onClick={() => window.location.href = 'mailto:Kbcowen21@yahoo.com?subject=Let\'s Connect&body=Hi Kevin,%0D%0A%0D%0AI came across your profile and would like to discuss...'}
              className="group w-full sm:w-auto bg-gradient-to-r from-[#3b82f6] to-[#60a5fa] hover:from-[#2563eb] hover:to-[#3b82f6] text-white px-6 py-5 sm:px-8 sm:py-6 text-base sm:text-lg shadow-xl shadow-blue-500/25 border-0"
              data-testid="contact-email-button"
            >
              <Mail className="w-4 h-4 sm:w-5 sm:h-5 mr-2" aria-hidden="true" />
              Send an Email
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => window.open(RESUME_URL, '_blank')}
              className="w-full sm:w-auto border-white/20 text-white hover:bg-white/10 hover:border-white/40 px-6 py-5 sm:px-8 sm:py-6 text-base sm:text-lg"
              data-testid="contact-download-resume-button"
            >
              <Download className="w-4 h-4 sm:w-5 sm:h-5 mr-2" aria-hidden="true" />
              Download Resume
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-6 sm:py-8 relative border-t border-white/5" role="contentinfo">
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-transparent border-2 border-[#3b82f6] rounded-lg flex items-center justify-center" aria-hidden="true">
              <span className="text-[10px] sm:text-xs font-bold text-white">K</span>
              <span className="text-[10px] sm:text-xs font-bold text-[#60a5fa]">C</span>
            </div>
            <p className="text-xs sm:text-sm text-white/40" data-testid="footer-copyright">
              © {new Date().getFullYear()} Kevin Cowen
            </p>
          </div>
          <nav className="flex items-center gap-4 sm:gap-6" aria-label="Footer navigation">
            <a 
              href="mailto:Kbcowen21@yahoo.com"
              className="text-xs sm:text-sm text-white/40 hover:text-white transition-colors duration-200 flex items-center gap-1.5 sm:gap-2"
              data-testid="footer-email"
              aria-label="Send email to Kevin Cowen"
            >
              <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4" aria-hidden="true" />
              Email
            </a>
            <a 
              href={LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs sm:text-sm text-white/40 hover:text-white transition-colors duration-200 flex items-center gap-1.5 sm:gap-2"
              data-testid="footer-linkedin"
              aria-label="Connect with Kevin Cowen on LinkedIn (opens in new tab)"
            >
              <Linkedin className="w-3.5 h-3.5 sm:w-4 sm:h-4" aria-hidden="true" />
              LinkedIn
            </a>
          </nav>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="min-h-screen bg-[#0a0c10] relative">
      {/* Persistent Blueprint Background */}
      <div className="fixed inset-0 bg-[#0a0c10] blueprint-grid pointer-events-none" aria-hidden="true" />
      
      {/* Content Layer */}
      <div className="relative z-10">
        <Header />
        <main>
          <Hero />
          <Highlights />
          <About />
          <Experience />
          <Education />
          <Contact />
        </main>
        <Footer />
      </div>
    </div>
  );
}

export default App;
